import { VehicleRowCardVM } from 'src/pages/ConvenienceDispatches/vehicleRowVM';

/**
 * operation の並び順に要素を並べ替える。要素の位置は、持っている operation のうち
 * 最初に現れるものの位置で決める。
 *
 * どの operation も並びに現れない要素（operation を持たないカードなど）は
 * 動かす手がかりが無いので、元の位置に残す。並びが同じなら元の配列を返す。
 */
export function reorderByOperationIds<T>(
  items: T[],
  operationIdsOf: (item: T) => readonly number[],
  orderedOperationIds: readonly number[],
): T[] {
  if (orderedOperationIds.length === 0) return items;

  const rankByOperationId = new Map<number, number>();
  orderedOperationIds.forEach((id, index) => {
    if (!rankByOperationId.has(id)) rankByOperationId.set(id, index);
  });

  const rankOf = (item: T): number | null => {
    const ranks = operationIdsOf(item)
      .map((id) => rankByOperationId.get(id))
      .filter((rank): rank is number => rank != null);
    return ranks.length === 0 ? null : Math.min(...ranks);
  };

  // 動かせる要素だけを抜き出して並べ替え、動かせない要素は元の添字に戻す
  const fixedByIndex = new Map<number, T>();
  const movable: { item: T; rank: number }[] = [];
  items.forEach((item, index) => {
    const rank = rankOf(item);
    if (rank == null) fixedByIndex.set(index, item);
    else movable.push({ item, rank });
  });
  if (movable.length === 0) return items;

  // Array#sort は安定なので、同じ rank の要素は元の順のまま
  const sorted = [...movable].sort((a, b) => a.rank - b.rank);

  const next: T[] = [];
  let movableIndex = 0;
  for (let index = 0; index < items.length; index += 1) {
    const fixed = fixedByIndex.get(index);
    next.push(fixed ?? sorted[movableIndex++].item);
  }

  // 参照が変わると React が全要素を作り直す。並びが同じなら元の配列を返す
  return next.every((item, index) => item === items[index]) ? items : next;
}

/**
 * 停車地を並べ替えたときの、行のカード列。
 *
 * VehicleRowVM のカードを並べ替えるのは、左リストのカード列と地図のルート線が
 * 同じ VM から作られるため。ここを差し替えると両方が同時に動く。
 * 展開パネルの停車地と番号マーカーは arrivalAt 順に組み直されるので、
 * reorderExpandedStopsByOperationIds で別に並べ替える。
 *
 * operation を持たないカード（再積み込み拠点・固定ルートの仮カード）は動かす
 * 手がかりが無いので、元の位置に残す。
 */
export function reorderRowCardsByOperationIds(
  cards: VehicleRowCardVM[],
  orderedOperationIds: readonly number[],
): VehicleRowCardVM[] {
  return reorderByOperationIds(cards, (card) => card.operations.map((op) => op.id), orderedOperationIds);
}
